import React, { useEffect, useRef, useState } from "react";
import client, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Loader2, Trash2, Pencil, Check, X, Phone, Building2 } from "lucide-react";

const TYPE_META = {
  hospital: { label: "Hospital", color: "#EF4444" },
  fire_station: { label: "Fire Station", color: "#F97316" },
  police_station: { label: "Police Station", color: "#3B82F6" },
  ems_station: { label: "EMS Station", color: "#10B981" },
  school: { label: "School", color: "#F59E0B" },
  federal_building: { label: "Federal Building", color: "#A78BFA" },
};

export const FacilitiesAdmin = () => {
  const [rows, setRows] = useState([]);
  const [seed, setSeed] = useState(null);
  const [type, setType] = useState("");
  const [st, setSt] = useState("");
  const [editId, setEditId] = useState(null);
  const [edit, setEdit] = useState({});
  const pollRef = useRef(null);

  const load = () => client.get("/facilities", { params: { type, state: st } })
    .then((r) => setRows(r.data.facilities)).catch(() => {});
  const pollStatus = async () => {
    try {
      const { data } = await client.get("/facilities/seed/status");
      setSeed(data);
      if (!data.running && pollRef.current) {
        clearInterval(pollRef.current); pollRef.current = null;
        if (data.finished_at) load();
      }
    } catch { /* transient */ }
  };
  useEffect(() => { load(); pollStatus(); return () => clearInterval(pollRef.current); }, [type, st]); // eslint-disable-line

  const runSeed = async () => {
    try {
      await client.post("/facilities/seed");
      toast.success("AI facility seeding started — Gemini is web-searching MD & PA facilities");
      pollRef.current = setInterval(pollStatus, 3000);
      pollStatus();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const saveEdit = async (fid) => {
    try {
      await client.patch(`/facilities/${fid}`, edit);
      toast.success("Facility updated");
      setEditId(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const del = async (f) => {
    if (!window.confirm(`Delete ${f.name}?`)) return;
    try { await client.delete(`/facilities/${f.id}`); toast.success("Facility removed"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const running = seed?.running;
  const pct = seed?.steps_total ? Math.round((seed.steps_done / seed.steps_total) * 100) : 0;
  const inp = "bg-[#141518] border border-[#2C2D35] px-2 py-1 text-xs font-mono text-white outline-none focus:border-[#3B82F6]";

  return (
    <div className="border border-[#2C2D35] rounded-md p-4 bg-[#101114]" data-testid="facilities-admin">
      <div className="flex items-center gap-2 flex-wrap mb-3">
        <Building2 size={16} className="text-[#22D3EE]" />
        <div className="font-head font-bold text-sm tracking-tight text-white">FACILITY DIRECTORY (MD / PA)</div>
        <span className="font-mono text-[10px] text-[#6B7280]">{rows.length} facilities · AI-seeded via Gemini web search</span>
        <div className="ml-auto flex items-center gap-2">
          <select data-testid="fac-type-filter" value={type} onChange={(e) => setType(e.target.value)} className={inp}>
            <option value="">All types</option>
            {Object.entries(TYPE_META).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
          </select>
          <select data-testid="fac-state-filter" value={st} onChange={(e) => setSt(e.target.value)} className={inp}>
            <option value="">MD + PA</option>
            <option value="MD">MD</option>
            <option value="PA">PA</option>
          </select>
          <button data-testid="fac-seed-btn" onClick={runSeed} disabled={running}
            className="px-3 py-1.5 text-[11px] font-head font-bold rounded-sm flex items-center gap-1.5 text-white disabled:opacity-60"
            style={{ background: running ? "#4B5563" : "#7C3AED" }}>
            {running ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
            {running ? "SEEDING…" : "RUN AI FACILITY SEED"}
          </button>
        </div>
      </div>

      {seed && (running || seed.finished_at) && (
        <div data-testid="fac-seed-status" className="mb-3 border border-[#2C2D35] bg-[#141518] p-2.5">
          <div className="flex items-center gap-2 font-mono text-[10px] text-[#9CA3AF]">
            {running
              ? <>SEARCHING: <span className="text-[#22D3EE]">{seed.current || "starting…"}</span></>
              : <span className="text-[#10B981]">SEED COMPLETE — finished {seed.finished_at?.slice(0, 16).replace("T", " ")} UTC</span>}
            <span className="ml-auto">{seed.steps_done}/{seed.steps_total} searches · +{seed.added} added · {seed.updated} refreshed</span>
          </div>
          <div className="h-1.5 bg-[#2C2D35] rounded mt-1.5 overflow-hidden">
            <div className="h-full transition-all" style={{ width: `${pct}%`, background: running ? "#7C3AED" : "#10B981" }} />
          </div>
          {(seed.errors || []).length > 0 && (
            <div className="font-mono text-[9px] text-[#F87171] mt-1">{seed.errors.length} search step(s) failed: {seed.errors[0]}</div>
          )}
        </div>
      )}

      <div className="max-h-96 overflow-y-auto border border-[#2C2D35]">
        <table className="w-full text-left">
          <thead className="sticky top-0 bg-[#141518]">
            <tr className="font-head text-[9px] text-[#6B7280] tracking-wider">
              <th className="px-2 py-1.5">TYPE</th><th className="px-2 py-1.5">NAME</th>
              <th className="px-2 py-1.5">ADDRESS</th><th className="px-2 py-1.5">CITY / COUNTY</th>
              <th className="px-2 py-1.5">ST</th><th className="px-2 py-1.5">PHONE</th><th className="px-2 py-1.5" />
            </tr>
          </thead>
          <tbody>
            {rows.map((f) => {
              const m = TYPE_META[f.facility_type] || { label: f.facility_type, color: "#9CA3AF" };
              const editing = editId === f.id;
              return (
                <tr key={f.id} data-testid={`fac-row-${f.id}`} className="border-t border-[#1F2026] font-mono text-[11px] text-[#C9CDD4]">
                  <td className="px-2 py-1.5"><span className="px-1.5 py-px rounded-sm text-[9px] font-bold" style={{ color: m.color, border: `1px solid ${m.color}55` }}>{m.label.toUpperCase()}</span></td>
                  <td className="px-2 py-1.5 text-white">{editing ? <input className={inp} value={edit.name} onChange={(e) => setEdit((p) => ({ ...p, name: e.target.value }))} /> : f.name}</td>
                  <td className="px-2 py-1.5">{editing ? <input className={inp} value={edit.address} onChange={(e) => setEdit((p) => ({ ...p, address: e.target.value }))} /> : f.address}</td>
                  <td className="px-2 py-1.5">{f.city}{f.county ? ` / ${f.county}` : ""}</td>
                  <td className="px-2 py-1.5">{f.state}</td>
                  <td className="px-2 py-1.5">
                    {editing ? <input className={inp} value={edit.phone} onChange={(e) => setEdit((p) => ({ ...p, phone: e.target.value }))} />
                      : f.phone ? <a href={`tel:${f.phone.replace(/[^\d+]/g, "")}`} className="text-[#4ade80] flex items-center gap-1 hover:underline"><Phone size={10} />{f.phone}</a>
                        : <span className="text-[#4B5563]">—</span>}
                  </td>
                  <td className="px-2 py-1.5">
                    <div className="flex items-center gap-1.5 justify-end">
                      {editing ? (
                        <>
                          <button data-testid={`fac-save-${f.id}`} onClick={() => saveEdit(f.id)} className="text-[#10B981]" aria-label="Save"><Check size={13} /></button>
                          <button onClick={() => setEditId(null)} className="text-[#9CA3AF]" aria-label="Cancel"><X size={13} /></button>
                        </>
                      ) : (
                        <>
                          <button data-testid={`fac-edit-${f.id}`} onClick={() => { setEditId(f.id); setEdit({ name: f.name, address: f.address, phone: f.phone }); }} className="text-[#3B82F6]" aria-label="Edit"><Pencil size={12} /></button>
                          <button data-testid={`fac-del-${f.id}`} onClick={() => del(f)} className="text-[#EF4444]" aria-label="Delete"><Trash2 size={12} /></button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
            {!rows.length && (
              <tr><td colSpan={7} className="px-3 py-4 font-mono text-xs text-[#6B7280]">
                No facilities yet — click RUN AI FACILITY SEED to have Gemini web-search real hospitals, fire/police/EMS stations, schools and federal buildings across Maryland and Pennsylvania.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
